/**
 * Pack/Options gating (Fabric theme)
 * - Options have tags: requires_pack:bronze / requires_pack:silver (can be multiple)
 * - Packs add line item property: _pack_tag = "pack:bronze" (or silver/gold/...)
 * - quick-add-component renders:
 *    data-requires-packs="pack:bronze|pack:silver"
 *    data-pack-gate="blocked|allowed"
 *
 * This script:
 * - Fetches cart via /cart.js
 * - Builds a Set of pack tags present (from line item property _pack_tag)
 * - Updates all quick-add components accordingly (no mixed UI states)
 * - Refreshes after add-to-cart + cart changes (drawer / cart page)
 */

(function () {
  // -----------------------------
  // Utils
  // -----------------------------
  function normalizeTag(v) {
    return String(v || "").trim().toLowerCase();
  }

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) return null;
    return await res.json();
  }

  async function getPackTagsFromCart() {
    const cart = await fetchCart();
    const set = new Set();
    if (!cart || !Array.isArray(cart.items)) return set;

    cart.items.forEach((item) => {
      // We rely on the line item property set when adding packs: properties._pack_tag
      const props = item && item.properties ? item.properties : null;
      const packTag = props && props._pack_tag ? normalizeTag(props._pack_tag) : "";
      if (packTag) set.add(packTag);
    });

    return set;
  }

  function setGatedState(el, allowed) {
    // Force clean state every time to avoid mixed UI
    el.dataset.packGate = allowed ? "allowed" : "blocked";
    el.classList.toggle("is-pack-gated", !allowed);

    // Toggle inline message if present
    const msg = el.querySelector(".pack-gate-message");
    if (msg) msg.style.display = allowed ? "none" : "block";

    // Disable/enable interaction for all relevant buttons inside the component
    // - .quick-add__button (Fabric)
    // - .add-to-cart-button (some variants)
    // - button[name="add"] (choose/add cases)
    const buttons = el.querySelectorAll(".quick-add__button, .add-to-cart-button, button[name='add']");
    buttons.forEach((btn) => {
      // Hard-disable when blocked (prevents submit handlers)
      if (btn.tagName === "BUTTON") btn.disabled = !allowed;

      // Also force pointer-events to avoid weird overlay states
      btn.style.pointerEvents = allowed ? "" : "none";
      btn.setAttribute("aria-disabled", allowed ? "false" : "true");
    });
  }

  // -----------------------------
  // Main refresh
  // -----------------------------
  async function refreshOptionsGate() {
    const gatedEls = document.querySelectorAll("[data-requires-packs], [data-requires-pack]");
    if (!gatedEls.length) return;

    const packs = await getPackTagsFromCart();

    gatedEls.forEach((el) => {
      const rawMulti = el.dataset.requiresPacks;
      const rawSingle = el.dataset.requiresPack;

      let requiredList = [];

      if (rawMulti && String(rawMulti).trim() !== "") {
        requiredList = String(rawMulti)
          .split("|")
          .map((s) => normalizeTag(s))
          .filter(Boolean);
      } else if (rawSingle && String(rawSingle).trim() !== "") {
        requiredList = [normalizeTag(rawSingle)];
      }

      // No requirement => allow
      if (!requiredList.length) {
        setGatedState(el, true);
        return;
      }

      // Allow if ANY required pack is present
      const allowed = requiredList.some((tag) => packs.has(tag));
      setGatedState(el, allowed);
    });
  }

  // -----------------------------
  // Hook into add-to-cart + cart updates
  // -----------------------------
  let refreshTimer = null;
  function scheduleRefresh(delay = 150) {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshOptionsGate().catch(() => {});
    }, delay);
  }

  // 1) Initial
  document.addEventListener("DOMContentLoaded", () => scheduleRefresh(0));

  // 2) After any fetch to /cart/add or /cart/change or /cart/update, refresh gating
  // This catches theme AJAX add-to-cart (product page, quick-add, drawer, etc.)
  const _fetch = window.fetch;
  window.fetch = async function (...args) {
    const res = await _fetch.apply(this, args);

    try {
      const url = typeof args[0] === "string" ? args[0] : (args[0] && args[0].url) ? args[0].url : "";
      if (
        url.includes("/cart/add") ||
        url.includes("/cart/change") ||
        url.includes("/cart/update") ||
        url.includes("/cart/clear")
      ) {
        // Wait a bit for DOM/components to update, then refresh
        scheduleRefresh(250);
      }
    } catch (e) {
      // ignore
    }

    return res;
  };

  // 3) Some themes dispatch custom events; we listen broadly too
  window.addEventListener("cart:updated", () => scheduleRefresh(150));
  document.addEventListener("cart:updated", () => scheduleRefresh(150));
  document.addEventListener("cart:refresh", () => scheduleRefresh(150));
  document.addEventListener("shopify:cart:change", () => scheduleRefresh(150));

  // 4) If cart drawer is used, mutations can change DOM without events → observe body
  const mo = new MutationObserver(() => scheduleRefresh(200));
  mo.observe(document.documentElement, { subtree: true, childList: true });
})();

// --------------------------------------------------
// Block product-page navigation for option cards
// --------------------------------------------------
document.addEventListener(
  "click",
  function (e) {
    const link = e.target.closest("a");
    if (!link) return;

    const optionCard = link.closest('.product-card[data-disable-product-link="true"]');
    if (!optionCard) return;

    // Allow explicit non-navigation controls if ever rendered as links
    if (
      link.closest(".quick-add") ||
      link.closest(".quick-add__button") ||
      link.closest('[data-allow-option-link="true"]')
    ) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    return false;
  },
  true
);